import { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "../api/client";
import { Card } from "./Card";
import { ItemSprite } from "./ItemSprite";
import { NpcPortrait, PortraitHatching } from "./NpcPortrait";
import { catalogToCardView } from "../logic/catalogToCardView";
import { formatGold, formatVoidJuice } from "../logic/currency";
import { renderInline } from "../logic/inlineMarkdown";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { simplePanelStyle } from "../theme/simplePanel";
import { primaryBtn, withBtnStyle } from "../theme/styles";
import { inertAttrs, inertOnClick } from "../logic/inert";

interface Props {
  shopId: string;
  npc: api.WorldShopBlock["npc"];
  /** Called after every successful buy so the parent can refresh
   *  user state (currency / pack count). */
  onPurchase?: (next: api.BuyResponse) => void;
}

export function ShopView({ shopId, npc, onPurchase }: Props) {
  const theme = pixelTheme;
  const { narrow } = useResponsiveLayout();
  const [shop, setShop] = useState<api.ShopState | null>(null);
  const [items, setItems] = useState<Map<string, api.ItemDefinition>>(new Map());
  const [cards, setCards] = useState<Map<string, api.CatalogCard>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [busySlot, setBusySlot] = useState<number | null>(null);

  // We need item definitions to render names / sprites. Fetch the
  // inventory endpoint just for the catalog slice. Card-stocked
  // shops also fetch each card_id once on first appearance — see the
  // separate effect below.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [s, inv] = await Promise.all([
          api.getShop(shopId),
          api.getInventory(),
        ]);
        if (cancelled) return;
        setShop(s);
        setItems(new Map(inv.items.map((i) => [i.id, i] as const)));
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load shop.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shopId]);

  // Lazy-load CatalogCard records for any card_ids appearing in the
  // shop's `card_entries`. The set changes on rotation rolls and
  // after a buy, so we re-resolve missing ids on every shop update.
  useEffect(() => {
    if (!shop) return;
    const missing = shop.card_entries
      .map((e) => e.card_id)
      .filter((id): id is string => Boolean(id))
      .filter((id) => !cards.has(id));
    if (missing.length === 0) return;

    let cancelled = false;
    void (async () => {
      try {
        const fetched = await Promise.all(missing.map((id) => api.getCardById(id)));
        if (cancelled) return;
        setCards((prev) => {
          const next = new Map(prev);
          for (const c of fetched) next.set(c.id, c);
          return next;
        });
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load card details.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shop, cards]);

  const onBuy = useCallback(
    async (itemId: string) => {
      setBusyItem(itemId);
      setError(null);
      try {
        const r = await api.buyFromShop(shopId, itemId);
        // Refresh local entries (server is authoritative) and notify.
        if (shop) {
          setShop({ ...shop, entries: r.entries });
        }
        onPurchase?.(r);
      } catch (e) {
        const msg =
          e instanceof api.ApiError && typeof e.body === "object" && e.body
            ? friendlyError((e.body as { error?: string }).error ?? null)
            : e instanceof Error
              ? e.message
              : "Buy failed.";
        setError(msg);
      } finally {
        setBusyItem(null);
      }
    },
    [onPurchase, shop, shopId],
  );

  const onBuyCard = useCallback(
    async (slotIndex: number) => {
      setBusySlot(slotIndex);
      setError(null);
      try {
        const r = await api.buyCardFromShop(shopId, slotIndex);
        if (shop) {
          setShop({ ...shop, card_entries: r.card_entries });
        }
        // Reuse the same balances-update side effect as item buys so
        // the header chip stays in sync.
        onPurchase?.({
          slots: [],
          balances: r.balances,
          entries: shop?.entries ?? [],
        });
      } catch (e) {
        const msg =
          e instanceof api.ApiError && typeof e.body === "object" && e.body
            ? friendlyError((e.body as { error?: string }).error ?? null)
            : e instanceof Error
              ? e.message
              : "Buy failed.";
        setError(msg);
      } finally {
        setBusySlot(null);
      }
    },
    [onPurchase, shop, shopId],
  );

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* Shopkeeper portrait + greeting */}
      <div
        style={{
          ...simplePanelStyle(theme),
          padding: 14,
          display: "grid",
          gridTemplateColumns: "120px 1fr",
          gap: 16,
          alignItems: "stretch",
        }}
      >
        <NpcPortrait faction={npc.faction} size={120} npcId={shopId}>
          <PortraitHatching />
        </NpcPortrait>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 9,
                color: theme.inkFaint,
                letterSpacing: theme.letterSpacing,
              }}
            >
              {(npc.role ?? "SHOPKEEPER").toUpperCase()}
            </div>
            <div
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 16,
                color: theme.ink,
                marginTop: 4,
                letterSpacing: theme.letterSpacing,
              }}
            >
              {(npc.name ?? "Unnamed").toUpperCase()}
            </div>
            {npc.greeting && (
              <div
                style={{
                  fontSize: 16,
                  color: theme.ink,
                  marginTop: 10,
                  lineHeight: 1.4,
                  whiteSpace: "pre-wrap",
                }}
              >
                {renderInline(npc.greeting.trim())}
              </div>
            )}
          </div>
          {shop && (
            <ResetCountdown initialSeconds={shop.resets_in_seconds} theme={theme} />
          )}
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: 10,
            border: `2px solid ${E32.scarlet}`,
            color: E32.scarlet,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
          }}
        >
          {error}
        </div>
      )}

      {/* Wares (item entries) — only render when there are any. */}
      {shop && shop.entries.length > 0 && (
        <section style={{ display: "grid", gap: 8 }}>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
              color: theme.inkFaint,
            }}
          >
            WARES — {shop.entries.length} ITEMS
          </div>
          <div
            style={{
              display: "grid",
              // Side-by-side wares on desktop, single column on narrow
              // viewports so each item card has room to breathe.
              gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",
              gap: 12,
            }}
          >
            {shop.entries.map((entry) => (
              <ShopEntryCard
                key={entry.item_id}
                entry={entry}
                item={items.get(entry.item_id) ?? null}
                busy={busyItem === entry.item_id}
                onBuy={() => onBuy(entry.item_id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Card stock — rotating shelf of rare/legendary cards. */}
      {shop && shop.card_entries.length > 0 && (
        <section style={{ display: "grid", gap: 8 }}>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
              color: theme.inkFaint,
            }}
          >
            CARD STOCK — {shop.card_entries.length} SLOTS · ROTATES DAILY
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: narrow
                ? "1fr"
                : `repeat(${shop.card_entries.length}, 1fr)`,
              gap: 12,
              alignItems: "stretch",
            }}
          >
            {shop.card_entries.map((entry) => (
              <ShopCardSlot
                key={entry.slot_index}
                entry={entry}
                card={entry.card_id ? (cards.get(entry.card_id) ?? null) : null}
                busy={busySlot === entry.slot_index}
                onBuy={() => onBuyCard(entry.slot_index)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ShopCardSlot({
  entry,
  card,
  busy,
  onBuy,
}: {
  entry: api.ShopCardEntry;
  card: api.CatalogCard | null;
  busy: boolean;
  onBuy: () => void;
}) {
  const theme = pixelTheme;
  const stockEmpty = entry.sold || entry.out_of_stock;
  const stockLabel = entry.sold
    ? "SOLD TODAY"
    : entry.out_of_stock
      ? "OUT OF STOCK"
      : entry.copies > 1
        ? "1 PAIR LEFT"
        : "1 LEFT";

  return (
    <div
      style={{
        ...simplePanelStyle(theme),
        padding: 14,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        alignItems: "center",
      }}
    >
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          letterSpacing: theme.letterSpacing,
          color:
            entry.rarity === "legendary"
              ? E32.amber
              : entry.rarity === "rare"
                ? E32.cobalt
                : theme.inkFaint,
        }}
      >
        {entry.rarity.toUpperCase()}
      </div>

      {card ? (
        <Card card={catalogToCardView(card)} size="normal" theme={theme} />
      ) : (
        <div
          style={{
            width: 180,
            height: 240,
            border: `2px dashed ${theme.panelEdge}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            color: theme.inkFaint,
            letterSpacing: theme.letterSpacing,
            textAlign: "center",
            padding: 10,
          }}
        >
          NO STOCK<br />TODAY
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <CostLabel cost={entry.cost} />
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            color: stockEmpty ? E32.scarlet : theme.inkFaint,
            letterSpacing: theme.letterSpacing,
          }}
        >
          {stockLabel}
        </span>
      </div>

      <button
        type="button"
        onClick={inertOnClick(stockEmpty || busy, onBuy)}
        {...inertAttrs(stockEmpty || busy)}
        {...withBtnStyle(primaryBtn(theme, stockEmpty || busy), {
          minWidth: 96,
          textAlign: "center",
        })}
      >
        {entry.sold
          ? "SOLD"
          : entry.out_of_stock
            ? "OUT"
            : busy
              ? "BUYING…"
              : "BUY"}
      </button>
    </div>
  );
}

function ResetCountdown({
  initialSeconds,
  theme,
}: {
  initialSeconds: number;
  theme: typeof pixelTheme;
}) {
  const [seconds, setSeconds] = useState(initialSeconds);
  useEffect(() => {
    setSeconds(initialSeconds);
    const id = window.setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [initialSeconds]);

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const fmt = `${pad(h)}:${pad(m)}:${pad(s)}`;

  return (
    <div
      style={{
        fontFamily: theme.fontDisplay,
        fontSize: 9,
        color: theme.inkFaint,
        letterSpacing: theme.letterSpacing,
      }}
    >
      SHOP RESETS IN {fmt}
    </div>
  );
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function ShopEntryCard({
  entry,
  item,
  busy,
  onBuy,
}: {
  entry: api.ShopEntryState;
  item: api.ItemDefinition | null;
  busy: boolean;
  onBuy: () => void;
}) {
  const theme = pixelTheme;
  const stockEmpty =
    entry.stock.kind !== "infinite" &&
    entry.remaining != null &&
    entry.remaining <= 0;

  const stockLabel = useMemo(() => {
    if (entry.stock.kind === "infinite") return null;
    if (stockEmpty) {
      return entry.stock.kind === "lifetime" ? "OUT OF STOCK" : "SOLD OUT TODAY";
    }
    return entry.stock.kind === "lifetime"
      ? `${entry.remaining}/${entry.stock.max} LEFT`
      : `${entry.remaining}/${entry.stock.max} TODAY`;
  }, [entry, stockEmpty]);

  return (
    <div
      style={{
        ...simplePanelStyle(theme),
        padding: 14,
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: 14,
      }}
    >
      <div
        style={{
          width: 96,
          height: 96,
          background: theme.bgDeep,
          border: `2px solid ${theme.panelEdge}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(0,0,0,0.05) 0 4px, transparent 4px 8px)",
        }}
      >
        <ItemSprite id={entry.item_id} size={72} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.ink,
            lineHeight: 1.3,
          }}
        >
          {(item?.name ?? entry.item_id).toUpperCase()}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CostLabel cost={entry.cost} />
          {stockLabel && (
            <span
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 9,
                color: stockEmpty ? E32.scarlet : theme.inkFaint,
                letterSpacing: theme.letterSpacing,
              }}
            >
              {stockLabel}
            </span>
          )}
        </div>
        {item?.description && (
          <div
            style={{
              fontSize: 14,
              color: theme.inkDim,
              lineHeight: 1.35,
              flex: 1,
            }}
          >
            {item.description}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={inertOnClick(stockEmpty || busy, onBuy)}
            {...inertAttrs(stockEmpty || busy)}
            {...withBtnStyle(primaryBtn(theme, stockEmpty || busy), {
              minWidth: 96,
              textAlign: "center",
            })}
          >
            {stockEmpty ? "SOLD OUT" : busy ? "BUYING…" : "BUY"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CostLabel({ cost }: { cost: api.ShopEntryState["cost"] }) {
  const theme = pixelTheme;
  const isJuice = cost.currency === "void_juice";
  return (
    <span
      style={{
        fontFamily: theme.fontDisplay,
        fontSize: 11,
        color: isJuice ? E32.mulberry : E32.oxblood,
        letterSpacing: theme.letterSpacing,
      }}
    >
      {isJuice ? formatVoidJuice(cost.amount) : `${formatGold(cost.amount)} GOLD`}
    </span>
  );
}

function friendlyError(code: string | null): string {
  switch (code) {
    case "out_of_stock":
      return "That item is sold out.";
    case "insufficient_funds":
      return "You can't afford that.";
    case "inventory_full":
      return "Your inventory is full.";
    case "shop_not_found":
      return "Shop not found.";
    case "item_not_in_shop":
      return "That item isn't in this shop any more.";
    case "guest_blocked":
      return "Guests can't buy from shops. Convert your account first.";
    case "already_sold":
      return "That card was already sold today.";
    case "card_no_longer_available":
      return "That card was claimed elsewhere — the slot is empty now.";
    case "no_card_inventory":
      return "This shop doesn't sell cards.";
    case "bad_slot":
      return "Unknown card slot.";
    default:
      return "Buy failed.";
  }
}
